import { ColumnDef, Row } from "@tanstack/react-table";
import { DataTableColumnHeader } from "@/components/shared/data-table/data-table-column-header";
import { Button } from "@/components/ui/button";
import { MoreHorizontal } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { SET_DEFAULT_PRICELIST, DELETE_ONE_PRICELIST } from "../priceListQueries";
import { toast } from "sonner";

type PriceList = {
  id: number;
  name: string;
  default: boolean;
  createdAt?: string;
  updatedAt?: string;
};

function DefaultPillCell({ row }: { row: Row<PriceList> }) {
  if (!row.original.default) return null;
  return (
    <span
      className="rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800"
      aria-label="Default pricelist"
    >
      Default pricelist
    </span>
  );
}

function RowActionsCell({ row }: { row: Row<PriceList> }) {
  const isDefault = row.original.default;

  const [setDefault, { loading: settingDefault }] = useMutation(SET_DEFAULT_PRICELIST, {
    refetchQueries: ["GET_PRICELISTS"],
    awaitRefetchQueries: true,
  });

  const [deleteOne, { loading: deleting }] = useMutation(DELETE_ONE_PRICELIST, {
    refetchQueries: ["GET_PRICELISTS"],
    awaitRefetchQueries: true,
  });

  const handleMakeDefault = async () => {
    if (isDefault) return;
    try {
      await setDefault({ variables: { id: row.original.id } });
      toast.success("Default pricelist updated");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to set default pricelist"
      );
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete pricelist "${row.original.name}"?`)) return;
    try {
      await deleteOne({ variables: { id: row.original.id } });
      toast.success("Pricelist deleted");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to delete pricelist"
      );
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-8 w-8 p-0" aria-label="Actions">
          <span className="sr-only">Open menu</span>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          onClick={handleMakeDefault}
          disabled={isDefault || settingDefault}
        >
          Make default
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={handleDelete}
          disabled={isDefault || deleting}
          className="text-destructive focus:text-destructive"
        >
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export const columns: ColumnDef<PriceList>[] = [
  {
    accessorKey: "id",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="ID" />
    ),
    cell: ({ row }) => (
      <div className="text-xs text-muted-foreground">{row.getValue("id")}</div>
    ),
    meta: { width: "50px" },
  },
  {
    accessorKey: "name",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Name" />
    ),
    cell: ({ row }: { row: Row<PriceList> }) => (
      <div className="font-medium">{row.getValue("name")}</div>
    ),
  },
  {
    accessorKey: "default",
    header: "Default",
    cell: ({ row }: { row: Row<PriceList> }) => <DefaultPillCell row={row} />,
    meta: { width: "150px" },
    enableSorting: false,
  },
  {
    id: "viewDetails",
    cell: ({ row }: { row: Row<PriceList> }) => {
      const navigate = useNavigate();
      return (
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            navigate(
              `/dashboard/b2b-accounts/price-lists/${row.getValue("id")}`
            )
          }
        >
          View Details
        </Button>
      );
    },
    meta: { width: "120px" },
    enableSorting: false,
  },
  {
    id: "actions",
    header: "",
    cell: ({ row }: { row: Row<PriceList> }) => <RowActionsCell row={row} />,
    enableSorting: false,
    meta: { width: "50px" },
  },
];
